import React, { useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { TrendingUp, TrendingDown } from '@mui/icons-material';
import type { DashboardCashFlow, DashboardChange, DashboardNetWorth } from '../../services/dashboard-service';
import { useBaseCurrency } from '../../stores/currency-store';
import { Figure, NavigateFn, SectionCard, monthLabel, num, relativeTime, shortDate, useMoney } from './shared';

/** A change chip: "+$367,503 (10.9%) vs 30 days". */
const Change: React.FC<{ label: string; change: DashboardChange | null }> = ({ label, change }) => {
  const money = useMoney();
  if (!change) {
    return (
      <Typography variant="caption" sx={{ color: 'var(--text-faint)' }}>
        {label}: not enough history
      </Typography>
    );
  }
  const amt = num(change.amount);
  const up = amt >= 0;
  const color = up ? 'var(--inflow-text)' : 'var(--outflow-text)';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} title={`Since ${shortDate(change.from)}`}>
      {up ? <TrendingUp sx={{ fontSize: 16, color }} /> : <TrendingDown sx={{ fontSize: 16, color }} />}
      <Typography className="fw-num" variant="body2" sx={{ color, fontWeight: 600 }}>
        {up ? '+' : '−'}{money(Math.abs(amt), { compact: true })}
        {change.percent != null && ` (${up ? '+' : ''}${change.percent}%)`}
      </Typography>
      <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>{label}</Typography>
    </Box>
  );
};

/**
 * Twelve month-ends and today as one thin line. Single series, so no legend:
 * the card title names it. Hovering a point reads its date and value.
 */
export const Sparkline: React.FC<{ points: { date: string; value: string }[] }> = ({ points }) => {
  const money = useMoney();
  const [hover, setHover] = useState<number | null>(null);
  const W = 320;
  const H = 64;
  const geo = useMemo(() => {
    const vals = points.map((p) => num(p.value));
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const span = max - min || 1;
    const step = points.length > 1 ? W / (points.length - 1) : 0;
    return vals.map((v, i) => ({ x: i * step, y: 4 + (H - 8) * (1 - (v - min) / span) }));
  }, [points]);
  if (points.length < 2) return null;
  const d = geo.map((p, i) => `${i ? 'L' : 'M'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const h = hover != null ? points[hover] : null;
  return (
    <Box sx={{ position: 'relative', mt: 1.5 }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none"
        role="img" aria-label="Net worth over the last twelve months"
        onMouseLeave={() => setHover(null)}
        onMouseMove={(e) => {
          const r = (e.currentTarget as SVGSVGElement).getBoundingClientRect();
          const i = Math.round(((e.clientX - r.left) / r.width) * (points.length - 1));
          setHover(Math.max(0, Math.min(points.length - 1, i)));
        }}>
        <path d={d} fill="none" stroke="var(--accent)" strokeWidth={2} vectorEffect="non-scaling-stroke"
          strokeLinejoin="round" strokeLinecap="round" />
        {hover != null && (
          <line x1={geo[hover].x} x2={geo[hover].x} y1={0} y2={H} stroke="var(--border-strong)"
            strokeWidth={1} vectorEffect="non-scaling-stroke" />
        )}
      </svg>
      <Typography variant="caption" className="fw-num"
        sx={{ color: 'var(--text-secondary)', display: 'block', textAlign: 'right', minHeight: 18 }}>
        {h ? `${shortDate(h.date)}: ${money(h.value, { compact: true })}` : '12 months'}
      </Typography>
    </Box>
  );
};

/** Section 2: where the household stands, and how that has moved. */
export const NetWorthSummary: React.FC<{ netWorth: DashboardNetWorth; onNavigate: NavigateFn }> = ({
  netWorth, onNavigate,
}) => {
  const money = useMoney();
  const base = useBaseCurrency();
  return (
    <SectionCard title="Net worth" onNavigate={onNavigate}
      action={{ label: 'Details', link: { to: '/net-worth' } }}
      subtitle={`Updated ${relativeTime(netWorth.last_updated_at)} · ${netWorth.account_count} accounts`}>
      <Typography className="fw-num" component="div"
        sx={{ fontSize: { xs: '1.9rem', md: '2.2rem' }, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.15 }}>
        {money(netWorth.total)}
      </Typography>
      <Typography variant="caption" sx={{ color: 'var(--text-faint)' }}>Total net worth ({base})</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mt: 1 }}>
        <Change label="vs 30 days" change={netWorth.change_30d} />
        <Change label="vs 1 year" change={netWorth.change_1y} />
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1.5, mt: 2 }}>
        <Figure label="Assets" value={money(netWorth.assets, { compact: true })} />
        <Figure label="Investments" value={money(netWorth.investments, { compact: true })} />
        <Figure label="Debts" value={money(netWorth.debts, { compact: true })} tone="out" />
      </Box>
      <Sparkline points={netWorth.sparkline} />
    </SectionCard>
  );
};

/**
 * Section 6: this month's money in and out. Savings transfers sit beside
 * spending, not inside it -- moving money to your own TFSA is not spending it.
 */
export const CashFlowSummary: React.FC<{ cashFlow: DashboardCashFlow; onNavigate: NavigateFn }> = ({
  cashFlow, onNavigate,
}) => {
  const money = useMoney();
  const income = num(cashFlow.income);
  const spend = num(cashFlow.spend);
  const saved = num(cashFlow.savings_transfers);
  const net = num(cashFlow.net);
  const max = Math.max(income, spend, saved, 1);
  const rows: { label: string; value: number; color: string }[] = [
    { label: 'Income', value: income, color: 'var(--inflow)' },
    { label: 'Spending', value: spend, color: 'var(--outflow)' },
    { label: 'To savings & investments', value: saved, color: 'var(--invest)' },
  ];
  return (
    <SectionCard title="Cash flow" subtitle={`${monthLabel(cashFlow.month)} to date`} onNavigate={onNavigate}
      action={{ label: 'Reports', link: { to: '/reports' } }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        {rows.map((r) => (
          <Box key={r.label}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
              <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>{r.label}</Typography>
              <Typography variant="body2" className="fw-num" sx={{ fontWeight: 600 }}>{money(r.value)}</Typography>
            </Box>
            <Box sx={{ height: 6, borderRadius: 3, background: 'var(--glass-2)', mt: 0.5, overflow: 'hidden' }}>
              <Box sx={{ height: '100%', width: `${(r.value / max) * 100}%`, background: r.color, borderRadius: 3 }} />
            </Box>
          </Box>
        ))}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mt: 2,
        pt: 1.5, borderTop: '1px solid var(--border-soft)' }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>Net (income − spending)</Typography>
        <Typography className="fw-num" sx={{ fontWeight: 700,
          color: net >= 0 ? 'var(--inflow-text)' : 'var(--outflow-text)' }}>
          {net >= 0 ? '+' : '−'}{money(Math.abs(net))}
        </Typography>
      </Box>
    </SectionCard>
  );
};
